import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, apiClient } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Dumbbell, UtensilsCrossed } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CoachingInsights } from '../components/CoachingInsights';
import { CoachPunchCard } from '../components/dashboard/CoachPunchCard';
import OnboardingWizard from '../components/OnboardingWizard';
import { UserAvatarWithCard } from '../components/UserAvatar';
import { NotificationsBloc } from '../components/dashboard/NotificationsBloc';
import { DailyQuoteCard } from '../components/dashboard/DailyQuoteCard';
import { BodyMetricsCard } from '../components/dashboard/BodyMetricsCard';
import { ActiveAlerts } from '../components/dashboard/ActiveAlerts';
import { MyGroupWeekCard } from '../components/dashboard/MyGroupWeekCard';
import { MacroNeedsCard } from '../components/dashboard/MacroNeedsCard';
import { WeeklyRankingsCard } from '../components/dashboard/WeeklyRankingsCard';
import { TodayExercisesCard } from '../components/dashboard/TodayExercisesCard';
import { TodayProgramCard } from '../components/dashboard/TodayProgramCard';

import { WeightChartCard } from '../components/dashboard/WeightChartCard';
import { TopSemaineCard } from '../components/dashboard/TopSemaineCard';
import { EngagementStatus } from '../components/challenge/EngagementAct';
import { Shield } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [myProgram, setMyProgram] = useState(null);
  const [todayExercises, setTodayExercises] = useState(null);
  const [weightHistory, setWeightHistory] = useState([]);
  const [challengeData, setChallengeData] = useState(null);
  const [defis, setDefis] = useState([]);
  const [deals, setDeals] = useState([]);
  const [weekPrograms, setWeekPrograms] = useState(null);
  const [myChallenge, setMyChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [profile, setProfile] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        apiClient.get('/summary/daily'),
        apiClient.get('/rankings/weekly'),
        apiClient.get('/notifications'),
        apiClient.get('/my-program'),
        apiClient.get('/metrics?limit=14'),
        apiClient.get('/exercises/today'),
        apiClient.get('/challenge/config'),
        apiClient.get('/challenges'),
        apiClient.get('/deals'),
        apiClient.get('/programs/all'),
        apiClient.get('/challenge/mon-profil'),
        apiClient.get('/user/profile'),
      ]);
      if (results[0].status === 'fulfilled') setSummary(results[0].value.data);
      if (results[1].status === 'fulfilled') setRankings(results[1].value.data);
      if (results[2].status === 'fulfilled') setNotifications(results[2].value.data);
      if (results[3].status === 'fulfilled') setMyProgram(results[3].value.data);
      if (results[4].status === 'fulfilled') setWeightHistory(results[4].value.data || []);
      if (results[5].status === 'fulfilled') setTodayExercises(results[5].value.data);
      if (results[6].status === 'fulfilled') setChallengeData(results[6].value.data);
      if (results[7].status === 'fulfilled') setDefis(results[7].value.data || []);
      if (results[8].status === 'fulfilled') setDeals(results[8].value.data || []);
      if (results[9].status === 'fulfilled') setWeekPrograms(results[9].value.data || []);
      if (results[10].status === 'fulfilled') setMyChallenge(results[10].value.data?.participant);
      if (results[11].status === 'fulfilled') {
        const p = results[11].value.data;
        setProfile(p);
        if (!p?.onboarding_complete && !sessionStorage.getItem('onboarding_skipped')) setShowOnboarding(true);
      }
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMarkRead = async () => {
    try { await apiClient.post('/notifications/read'); setNotifications(prev => prev.map(n => ({ ...n, read: true }))); } catch {}
  };

  const handleReadOne = async (n) => {
    if (!n.read) {
      try { await apiClient.post(`/notifications/read/${n.id}`); setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x)); } catch {}
    }
    if (n.link_target) navigate(n.link_target);
  };

  const weightChartData = useMemo(() =>
    [...weightHistory].reverse().map(m => ({
      date: new Date(m.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      kg: m.weight
    })), [weightHistory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (showOnboarding) {
    return <OnboardingWizard userName={user?.name} onComplete={() => { setShowOnboarding(false); fetchData(); }} />;
  }

  const mealsData = summary?.meals || { calories: 0, protein: 0, carbs: 0, fat: 0, meal_count: 0 };
  const macroTargets = summary?.macro_targets || { calories: 2200, protein: 150, carbs: 300, fat: 80 };
  const macroRemaining = summary?.macro_remaining || { calories: 2200, protein: 150, carbs: 300, fat: 80 };
  const metricsData = summary?.metrics || {};
  const myActiveChallenges = myProgram?.my_challenges || [];
  const activeDefis = defis.filter(d => !d.locked && d.participants?.length > 0).slice(0, 3);
  const myOrders = deals.filter(d => d.orders?.some(o => o.user_id === user?.id)).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#0F0F0F] pb-24">
      <header className="bg-[#0F0F0F] px-5 pt-6 pb-3">
        <div className="flex items-center gap-3">
          <UserAvatarWithCard
            user={user}
            profile={profile}
            isChallenger={!!challengeData?.actif}
            size="lg"
          />
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-gradient-meute">La Meute</p>
            <h1 className="text-4xl text-white leading-none mt-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }} data-testid="dashboard-greeting">
              {user?.name || 'Athlete'}
            </h1>
          </div>
        </div>
        <DailyQuoteCard />
      </header>

      <main className="px-5 space-y-4 animate-fade-in">
        {/* Prochaine pesee - visible pour tout le monde */}
        {challengeData?.actif && (() => {
          const today = new Date(Date.now() - 10 * 3600000).toISOString().slice(0, 10);
          const dates = [
            { label: 'S2', date: challengeData.date_s2 },
            { label: 'S4', date: challengeData.date_s4 },
            { label: 'S6', date: challengeData.date_s6 },
            { label: 'S8', date: challengeData.date_s8 },
            { label: 'S10', date: challengeData.date_s10 },
            { label: 'S12', date: challengeData.date_s12 },
          ];
          const next = dates.find(d => d.date >= today);
          if (!next) return null;
          const daysUntil = Math.ceil((new Date(next.date) - new Date()) / 86400000);
          const urgency = daysUntil <= 1 ? 'bg-red-500/15 border-red-500/30 text-red-400' : daysUntil <= 3 ? 'bg-[#FFB800]/15 border-[#FFB800]/30 text-[#FFB800]' : 'bg-[#3b82f6]/10 border-[#3b82f6]/20 text-[#3b82f6]';
          return (
            <div className={`${urgency} border rounded-lg p-3 flex items-center gap-3`} data-testid="next-pesee-banner">
              <span className="text-xl">{daysUntil <= 1 ? '🔥' : daysUntil <= 3 ? '⚠️' : '📅'}</span>
              <div className="flex-1">
                <p className="text-sm font-bold">Pesee {next.label} {daysUntil === 0 ? "AUJOURD'HUI !" : daysUntil === 1 ? 'DEMAIN !' : `dans ${daysUntil} jours`}</p>
                <p className="text-[10px] opacity-70">{new Date(next.date + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} · A jeun, meme balance</p>
              </div>
              <span className="font-mono text-lg font-bold">J-{daysUntil}</span>
            </div>
          );
        })()}

        {notifications.length > 0 && (
          <NotificationsBloc notifs={notifications} onMarkAllRead={handleMarkRead} onReadOne={handleReadOne} />
        )}

        {myChallenge && myChallenge.caution_validated === true && (
          <EngagementStatus userName={user?.name} />
        )}
        {myChallenge && !myChallenge.caution_validated && (
          <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3 flex items-center gap-3" data-testid="engaged-banner">
            <span className="text-lg"><Shield className="w-5 h-5 text-[#FFB800]" /></span>
            <p className="text-xs text-[#FFB800]">Challenge — Caution en attente de validation</p>
          </div>
        )}

        <BodyMetricsCard metricsData={metricsData} />

        <CoachPunchCard />



        <MacroNeedsCard mealsData={mealsData} macroTargets={macroTargets} macroRemaining={macroRemaining} trainingBurned={summary?.training_burned || 0}
          profileNutrition={summary?.profile_nutrition}
          onTrainingUpdate={(val) => setSummary(prev => ({ ...prev, training_burned: val, macro_remaining: { ...prev.macro_remaining, calories: prev.macro_targets.calories - (prev.meals?.calories || 0) + val } }))} />

        <ActiveAlerts activeDefis={activeDefis} myOrders={myOrders} challengeData={challengeData} myActiveChallenges={myActiveChallenges} userId={user?.id} />

        <MyGroupWeekCard userId={user?.id} userName={user?.name} />

        <TodayProgramCard myProgram={myProgram} />

        <TodayExercisesCard todayExercises={todayExercises} />

        <CoachingInsights />

        <TopSemaineCard />

        <WeightChartCard weightChartData={weightChartData} currentWeight={metricsData.weight} />

        {mealsData.meal_count === 0 && !todayExercises?.totals?.exercise_count && !myProgram?.assigned && (weekPrograms || []).length === 0 && activeDefis.length === 0 && !metricsData.weight && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-8 text-center" data-testid="empty-state">
            <Dumbbell className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm mb-3">Aucune activite aujourd'hui</p>
            <div className="flex gap-3 justify-center">
              <Link to="/nutrition" className="px-4 py-2 bg-[#FF6B35]/15 border border-[#FF6B35]/30 rounded-lg text-[#FF6B35] text-sm font-semibold flex items-center gap-1.5" data-testid="empty-add-meal">
                <UtensilsCrossed className="w-4 h-4" /> Ajouter un repas
              </Link>
              <Link to="/entrainement?tab=exercices" className="px-4 py-2 bg-[#FFB800]/15 border border-[#FFB800]/30 rounded-lg text-[#FFB800] text-sm font-semibold flex items-center gap-1.5" data-testid="empty-add-exercise">
                <Dumbbell className="w-4 h-4" /> Seance de sport
              </Link>
            </div>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
